import {
  EMAIL_REGEX,
  URL_REGEX,
  isValidIp,
  isValidIpOrCidrOrRange,
  validationResult,
} from "./patterns";
import { validateCharPolicy } from "./charPolicies";
import { normalizeNameText } from "./safeText";
import * as Yup from "yup";
import {
  passwordChangeConfirmField,
  passwordChangeField,
  runFieldValidation,
  validateBugDescription,
  validateBugTitle,
  validateDescription,
  validateFirstName,
  validateLastName,
  validateName,
  validateOrganisationName,
  roleNameField,
  validateHostnameGlob,
} from "./yupCommonFields";

const SPIKE_ALERT_TYPES = new Set(["WAF_SPIKE", "SMART_PARSE_SPIKE", "BOT_SURGE", "RL_SPIKE"]);

export function validateAlertConditions(alertType, conditions = {}) {
  const errors = {};
  if (SPIKE_ALERT_TYPES.has(alertType)) {
    const threshold = parseInt(conditions.threshold, 10);
    const windowMinutes = parseInt(conditions.window_minutes, 10);
    if (!Number.isFinite(threshold) || threshold < 1) {
      errors.threshold = "Threshold must be at least 1";
    }
    if (!Number.isFinite(windowMinutes) || windowMinutes < 1) {
      errors.window_minutes = "Window must be at least 1 minute";
    } else if (windowMinutes > 10080) {
      errors.window_minutes = "Window must be at most 10080 minutes";
    }
  }
  if (alertType === "WSS_DROP") {
    const drop = parseInt(conditions.drop_threshold, 10);
    if (!Number.isFinite(drop) || drop < 1) {
      errors.drop_threshold = "Drop threshold must be at least 1";
    }
  }
  if (alertType === "CERT_EXPIRY") {
    if (!Array.isArray(conditions.days_before) || conditions.days_before.length === 0) {
      errors.days_before = "Add at least one days-before value";
    }
  }
  if (alertType === "ENGINE_HEALTH") {
    if (!Array.isArray(conditions.statuses) || conditions.statuses.length === 0) {
      errors.statuses = "Select at least one engine status";
    }
  }
  return errors;
}

export function validateAlertRuleForm(edit) {
  const errors = {};
  const nameResult = validateName(edit?.name);
  if (!nameResult.valid) errors.name = nameResult.message;

  const channels = edit?.channels || [];
  if (channels.includes("EMAIL")) {
    const emails = edit?.notifyEmails || [];
    if (emails.length > 0) {
      const bad = emails.find((e) => !EMAIL_REGEX.test(String(e).trim()));
      if (bad) errors.notifyEmails = `Invalid email address: ${bad}`;
    }
  }

  if (channels.includes("WEBHOOK")) {
    const url = String(edit?.webhookUrl ?? "").trim();
    if (!url) errors.webhookUrl = "Webhook URL is required when Webhook channel is enabled";
    else if (!URL_REGEX.test(url)) errors.webhookUrl = "Enter a valid webhook URL";
  }

  const cooldown = parseInt(edit?.cooldownMinutes, 10);
  if (!Number.isFinite(cooldown) || cooldown < 1 || cooldown > 10080) {
    errors.cooldownMinutes = "Cooldown must be between 1 and 10080 minutes";
  }

  Object.assign(errors, validateAlertConditions(edit?.alertType, edit?.conditions));

  const desc = String(edit?.description ?? "").trim();
  if (desc) {
    const descResult = validateDescription(desc);
    if (!descResult.valid) errors.description = descResult.message;
  }

  const valid = Object.keys(errors).length === 0;
  return { valid, errors };
}

export function validateProfileForm({ firstName, lastName, organisation }) {
  const errors = {};
  const fnResult = validateFirstName(firstName);
  if (!fnResult.valid) errors.firstName = fnResult.message;
  const lnResult = validateLastName(lastName);
  if (!lnResult.valid) errors.lastName = lnResult.message;
  const orgResult = validateOrganisationName(organisation);
  if (!orgResult.valid) errors.organisation = orgResult.message;
  return { valid: Object.keys(errors).length === 0, errors };
}

export function validatePasswordChangeForm({ password, confirmPassword }) {
  const errors = {};
  try {
    Yup.object({
      password: passwordChangeField,
      confirmPassword: passwordChangeConfirmField("password"),
    }).validateSync({ password, confirmPassword }, { abortEarly: false });
  } catch (err) {
    if (err.name !== "ValidationError") throw err;
    for (const e of err.inner?.length ? err.inner : [err]) {
      if (e.path) errors[e.path] = e.message;
    }
  }
  return { valid: Object.keys(errors).length === 0, errors };
}

export function validateApiKeyForm({ name, duration, permissions }) {
  const errors = {};
  const nameResult = validateName(name);
  if (!nameResult.valid) errors.name = nameResult.message;
  const d = parseInt(duration, 10);
  if (!Number.isFinite(d) || d < 1) errors.duration = "Duration must be at least 1 day";
  if (!Array.isArray(permissions) || permissions.length === 0) {
    errors.permissions = "At least 1 permission is required";
  }
  return { valid: Object.keys(errors).length === 0, errors };
}

export function validateSuperUserSelection(userId) {
  if (!userId) return validationResult(false, "Please select an administrator");
  return validationResult(true);
}

export function validateIpRestrictionEnableSave(enabled, ipCount) {
  if (enabled && ipCount === 0) {
    return validationResult(false, "Add at least one IP address when restrictions are enabled");
  }
  return validationResult(true);
}

/** IP restriction drawer — always requires at least one IP in the list before Save. */
export function validateIpRestrictionDrawerSave(ipCount) {
  const count = Number(ipCount) || 0;
  if (count < 1) {
    return validationResult(false, "Add at least one IP address, range, or CIDR before saving");
  }
  return validationResult(true);
}

export function validateIpRestrictionEntry(value) {
  const v = String(value ?? "").trim();
  if (!v) return validationResult(false, "IP address, range, or CIDR is required");
  if (!isValidIpOrCidrOrRange(v)) {
    return validationResult(false, "Enter a valid IP, CIDR, or range (e.g. 10.0.0.0/24)");
  }
  return validationResult(true);
}

export function validateSessionTimeoutMinutes(value, { min = 5, max = 43200 } = {}) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n)) return validationResult(false, "Enter a valid number of minutes");
  if (n < min || n > max) {
    return validationResult(false, `Must be between ${min} and ${max} minutes`);
  }
  return validationResult(true);
}

export function validateProjectName(name) {
  return validateName(name);
}

export function validateRoleName(name) {
  return runFieldValidation(roleNameField, name ?? "");
}

export function validateRoleDescription(description) {
  return validateDescription(description);
}

export function validateBugReportForm({ title, description }) {
  const errors = {};
  const titleResult = validateBugTitle(title);
  if (!titleResult.valid) errors.title = titleResult.message;
  const descResult = validateBugDescription(description);
  if (!descResult.valid) errors.description = descResult.message;
  return { valid: Object.keys(errors).length === 0, errors };
}

export function validateRuleName(name) {
  return validateName(name);
}

export function validateOrganisationDisplayName(value) {
  const s = normalizeNameText(value, { trim: true });
  if (!s) return validationResult(false, "Display name is required");
  if (s.length > 25) return validationResult(false, "Display name must be 25 characters or fewer");
  return validateCharPolicy("organisationName", s, { allowEmpty: false });
}

export function validateLicenseRequestMessage(value) {
  const s = String(value ?? "").trim();
  if (!s) return validationResult(true);
  if (s.length > 1000) return validationResult(false, "Message must be 1000 characters or fewer");
  return validateCharPolicy("plainText", s);
}

export function validateSiteHostname(value) {
  return validateHostnameGlob(value, { required: true });
}

export function validateSiteOrigin(value) {
  const v = String(value ?? "").trim();
  if (!v) return validationResult(false, "Origin is required");
  if (isValidIp(v)) return validationResult(true);
  return validateHostnameGlob(v, { required: true });
}
